import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  SITE_NAME,
  SITE_PUBLISHED_DATE,
  OG_IMAGE_PATH,
  TWITTER_IMAGE_PATH,
  absoluteUrl,
} from '../config/site';
import { getBuildDate } from '../seo/schema';

export interface PageSEOProps {
  title: string;
  description: string;
  /** Route path beginning with '/', used for the canonical URL */
  path: string;
  keywords?: string;
  /** Extra JSON-LD nodes appended to the page's @graph */
  schemaNodes?: object[];
  /** Schema.org type for the page itself */
  pageType?: 'WebPage' | 'Article' | 'TechArticle';
}

const PageSEO: React.FC<PageSEOProps> = ({
  title,
  description,
  path,
  keywords,
  schemaNodes = [],
  pageType = 'TechArticle',
}) => {
  const canonical = absoluteUrl(path);

  const graph = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': pageType,
        headline: title,
        description,
        url: canonical,
        datePublished: SITE_PUBLISHED_DATE,
        dateModified: getBuildDate(),
        author: { '@type': 'Organization', name: SITE_NAME },
        publisher: { '@type': 'Organization', name: SITE_NAME },
        mainEntityOfPage: canonical,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: absoluteUrl('/'),
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: title,
            item: canonical,
          },
        ],
      },
      ...schemaNodes,
    ],
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="article" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteUrl(OG_IMAGE_PATH)} />
      <meta property="og:site_name" content={SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteUrl(TWITTER_IMAGE_PATH)} />

      <script type="application/ld+json">{JSON.stringify(graph)}</script>
    </Helmet>
  );
};

export default PageSEO;
